/**
 * Zero-knowledge proof verification — `POST /zk/verify`.
 *
 * Server returns 204 on success, 422 on verification failure. We
 * lift the result into a plain `{ ok, detail }` object the UI can
 * show without caring about HTTP semantics.
 *
 * Proof generation (`POST /zk/prove`) is DEFERRED per server spec —
 * the UI will delegate prove calls to domain adapters, not to this
 * module.
 */

import type { SignedZkProof } from '../types';
import { ApiHttpError, apiFetch } from '../http';

export interface ZkVerifyResult {
  ok: boolean;
  /** Human-readable detail on failure — empty on success. */
  detail: string;
}

export async function verifyZkProof(
  proof: SignedZkProof,
): Promise<ZkVerifyResult> {
  try {
    await apiFetch<void>('/zk/verify', { method: 'POST', body: proof });
    return { ok: true, detail: '' };
  } catch (err) {
    if (err instanceof ApiHttpError && err.status === 422) {
      return { ok: false, detail: err.detail ?? err.title };
    }
    throw err;
  }
}
